/**
 * Shared Roma frame finish picker for PDP, wall visualizer, and Mockup Studio.
 *
 * Classic | Elite segment, finish-family chips (collapses 6½″/7″ twins),
 * reconstructed mini-frame thumbs, desktop/focus hover enlarge.
 *
 * Host mounts onto [data-frame-swatches] via GWFramePicker.mount(host, options).
 */
(function (root) {
  'use strict';

  var BAND_CLASSIC = 'classic';
  var BAND_ELITE = 'elite';

  function finishFamilyFor(frame) {
    if (!frame) return '';
    if (frame.finishFamily) return String(frame.finishFamily);
    if (frame.finish) return String(frame.finish);
    var name = String(frame.name || '');
    if (frame.collection === 'Tabacchino') {
      return name.replace(/^Tabacchino\s+/i, '').replace(/\s*\(6\s*½\s*″?\)\s*$/i, '').trim();
    }
    if (frame.collection === 'Elite') {
      return name
        .replace(/^Elite\s+/i, '')
        .replace(/\s*\(6\s*½\s*″?\)\s*$/i, '')
        .trim();
    }
    return name.replace(/\s*\(6\s*½\s*″?\)\s*$/i, '').trim();
  }

  function bandFor(frame) {
    if (!frame) return null;
    if (frame.collection === 'Elite') return BAND_ELITE;
    if (frame.collection === 'Arber' || frame.collection === 'Tabacchino') return BAND_CLASSIC;
    return null;
  }

  function formatFaceWidth(inches) {
    var n = Number(inches);
    if (!isFinite(n)) return '';
    if (Math.abs(n - 6.5) < 0.01) return '6½″';
    if (Math.abs(n - 3.5) < 0.01) return '3½″';
    if (Math.round(n) === n) return n + '″';
    return String(n).replace(/\.0$/, '') + '″';
  }

  function isAvailable(frame) {
    return !frame || frame.available !== false;
  }

  function familiesForBand(frames, band, includeUnavailable) {
    var list = (frames || []).filter(function (frame) {
      if (bandFor(frame) !== band) return false;
      if (!includeUnavailable && !isAvailable(frame)) return false;
      return true;
    });
    var byFamily = {};
    var order = [];
    list.forEach(function (frame) {
      var key = finishFamilyFor(frame);
      if (!key) return;
      if (!byFamily[key]) {
        byFamily[key] = {
          family: key,
          label: key,
          band: band,
          collection: frame.collection,
          frames: [],
          available: false,
        };
        order.push(key);
      }
      byFamily[key].frames.push(frame);
      if (isAvailable(frame)) byFamily[key].available = true;
    });
    return order.map(function (key) {
      var entry = byFamily[key];
      entry.frames.sort(function (a, b) {
        return Number(b.faceWidthInches || 0) - Number(a.faceWidthInches || 0);
      });
      entry.widths = entry.frames.map(function (f) {
        return {
          id: f.id,
          faceWidthInches: f.faceWidthInches,
          label: formatFaceWidth(f.faceWidthInches),
          available: isAvailable(f),
        };
      });
      entry.hasWidthToggle = entry.widths.length > 1;
      entry.defaultFrame = entry.frames.filter(isAvailable)[0] || entry.frames[0] || null;
      return entry;
    });
  }

  function classicSubgroups(families) {
    var arber = [];
    var tabacchino = [];
    (families || []).forEach(function (family) {
      if (family.collection === 'Tabacchino') tabacchino.push(family);
      else arber.push(family);
    });
    var groups = [];
    if (arber.length) groups.push({ key: 'arber', label: 'Classic · 3½″', families: arber });
    if (tabacchino.length) {
      groups.push({ key: 'tabacchino', label: 'Classic · 4″', families: tabacchino });
    }
    return groups;
  }

  function pickFrameInFamily(family, preferId, preferFace) {
    if (!family || !family.frames || !family.frames.length) return null;
    var i;
    if (preferId) {
      for (i = 0; i < family.frames.length; i += 1) {
        if (family.frames[i].id === preferId) return family.frames[i];
      }
    }
    if (preferFace != null) {
      for (i = 0; i < family.frames.length; i += 1) {
        if (Math.abs(Number(family.frames[i].faceWidthInches) - Number(preferFace)) < 0.01) {
          return family.frames[i];
        }
      }
    }
    return family.defaultFrame || family.frames[0];
  }

  function findFrame(frames, id) {
    for (var i = 0; i < (frames || []).length; i += 1) {
      if (frames[i].id === id) return frames[i];
    }
    return null;
  }

  function findFamily(families, frameId) {
    for (var i = 0; i < (families || []).length; i += 1) {
      for (var j = 0; j < families[i].frames.length; j += 1) {
        if (families[i].frames[j].id === frameId) return families[i];
      }
    }
    return null;
  }

  function prefersFinePointer() {
    try {
      return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch (e) {
      return true;
    }
  }

  function isNarrow() {
    try {
      return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    } catch (e) {
      return false;
    }
  }

  function canShowPopover() {
    return prefersFinePointer() && !isNarrow();
  }

  /**
   * @param {HTMLElement} host
   * @param {object} options
   * @param {object[]} options.frames
   * @param {string} options.selectedId
   * @param {function} options.assetUrl  (filename) => url
   * @param {function} options.onSelect  (frame) => void
   * @param {boolean} [options.includeUnavailable]
   * @param {string} [options.density] 'pdp' | 'viz'
   * @param {string} [options.hint]
   */
  function mount(host, options) {
    if (!host || !options) return null;
    var frames = options.frames || [];
    var includeUnavailable = !!options.includeUnavailable;
    var assetUrl = options.assetUrl || function (f) { return f || ''; };
    var onSelect = options.onSelect || function () {};
    var density = options.density === 'viz' ? 'viz' : 'pdp';
    var selectedId = options.selectedId || (frames[0] && frames[0].id) || '';
    var selected = findFrame(frames, selectedId);
    var band = bandFor(selected) || BAND_CLASSIC;
    var preferFace = selected ? selected.faceWidthInches : 7;
    var activeOptionId = null;
    var popover = null;
    var popoverTimer = null;

    host.classList.add('gw-frame-picker');
    host.classList.add('gw-frame-picker--' + density);
    host.setAttribute('data-gw-frame-picker', 'true');

    function visibleFamilies() {
      return familiesForBand(frames, band, includeUnavailable);
    }

    function ensureSelection() {
      var families = visibleFamilies();
      var family = findFamily(families, selectedId);
      if (!family && families.length) family = families[0];
      if (!family) {
        selectedId = '';
        selected = null;
        return;
      }
      selected = pickFrameInFamily(family, selectedId, preferFace);
      selectedId = selected ? selected.id : '';
      if (selected) preferFace = selected.faceWidthInches;
    }

    function hidePopover() {
      if (popoverTimer) {
        clearTimeout(popoverTimer);
        popoverTimer = null;
      }
      if (popover && popover.parentNode) popover.parentNode.removeChild(popover);
      popover = null;
    }

    function showPopover(btn, frame) {
      hidePopover();
      if (!canShowPopover() || !frame) return;
      var hoverSrc = assetUrl(frame.hoverAsset || frame.thumbAsset || frame.swatchAsset);
      if (!hoverSrc) return;
      popover = document.createElement('div');
      popover.className = 'gw-frame-picker__popover';
      popover.setAttribute('role', 'presentation');
      var img = document.createElement('img');
      img.src = hoverSrc;
      img.alt = '';
      popover.appendChild(img);
      document.body.appendChild(popover);
      var rect = btn.getBoundingClientRect();
      var size = 160;
      var left = rect.left + rect.width / 2 - size / 2;
      var top = rect.top - size - 10;
      if (top < 8) top = rect.bottom + 10;
      left = Math.max(8, Math.min(left, window.innerWidth - size - 8));
      popover.style.width = size + 'px';
      popover.style.height = size + 'px';
      popover.style.left = left + 'px';
      popover.style.top = top + 'px';
    }

    function schedulePopover(btn, frame) {
      hidePopover();
      if (!canShowPopover()) return;
      popoverTimer = setTimeout(function () {
        showPopover(btn, frame);
      }, 120);
    }

    function commitFrame(frame, announce) {
      if (!frame) return;
      if (!includeUnavailable && !isAvailable(frame)) return;
      selected = frame;
      selectedId = frame.id;
      preferFace = frame.faceWidthInches;
      band = bandFor(frame) || band;
      render();
      if (announce !== false) onSelect(frame);
    }

    function setBand(next) {
      if (next === band) return;
      band = next;
      var families = visibleFamilies();
      var family = families[0];
      if (family) {
        selected = pickFrameInFamily(family, null, preferFace);
        selectedId = selected ? selected.id : '';
        if (selected) preferFace = selected.faceWidthInches;
      } else {
        selected = null;
        selectedId = '';
      }
      render();
      if (selected) onSelect(selected);
    }

    function chipButton(family, listboxId) {
      var frame = pickFrameInFamily(family, selectedId, preferFace);
      var isActive = !!(selected && finishFamilyFor(selected) === family.family && bandFor(selected) === family.band);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'gw-frame-picker__chip' +
        (isActive ? ' is-active' : '') +
        (!family.available ? ' is-preview-only' : '');
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.setAttribute('data-family', family.family);
      btn.setAttribute('data-frame-id', frame ? frame.id : '');
      var faceLabel = frame ? formatFaceWidth(frame.faceWidthInches) : '';
      var label = family.label + (faceLabel ? ' · ' + faceLabel : '');
      if (!family.available) label += ' · Preview only';
      btn.setAttribute('aria-label', label);
      btn.title = label;
      if (isActive) {
        btn.id = listboxId + '-active';
        activeOptionId = btn.id;
      }
      btn.tabIndex = isActive ? 0 : -1;

      var img = document.createElement('img');
      img.className = 'gw-frame-picker__chip-img';
      img.alt = '';
      img.loading = 'lazy';
      img.src = assetUrl(
        (frame && (frame.thumbAsset || frame.swatchAsset || frame.sampleAsset)) || ''
      );
      btn.appendChild(img);

      var name = document.createElement('span');
      name.className = 'gw-frame-picker__chip-name';
      name.textContent = family.label;
      btn.appendChild(name);

      if (!family.available) {
        var note = document.createElement('span');
        note.className = 'gw-frame-picker__chip-note';
        note.textContent = 'Preview only';
        btn.appendChild(note);
      }

      btn.addEventListener('click', function () {
        commitFrame(frame);
      });
      btn.addEventListener('mouseenter', function () {
        schedulePopover(btn, frame);
      });
      btn.addEventListener('mouseleave', hidePopover);
      btn.addEventListener('focus', function () {
        schedulePopover(btn, frame);
      });
      btn.addEventListener('blur', hidePopover);

      return btn;
    }

    function renderWidthToggle(family) {
      if (!family || !family.hasWidthToggle || !selected) return null;
      if (finishFamilyFor(selected) !== family.family) return null;
      var wrap = document.createElement('div');
      wrap.className = 'gw-frame-picker__width';
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', 'Face width');
      family.widths.forEach(function (width) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
          'gw-frame-picker__width-btn' +
          (selected.id === width.id ? ' is-active' : '');
        btn.textContent = width.label;
        btn.setAttribute('aria-pressed', selected.id === width.id ? 'true' : 'false');
        btn.addEventListener('click', function () {
          var frame = findFrame(family.frames, width.id);
          commitFrame(frame);
        });
        wrap.appendChild(btn);
      });
      return wrap;
    }

    function renderFaceCaption(family) {
      if (!family || family.hasWidthToggle || !selected) return null;
      if (finishFamilyFor(selected) !== family.family) return null;
      var p = document.createElement('p');
      p.className = 'gw-frame-picker__face-caption';
      p.textContent = 'Face ' + formatFaceWidth(selected.faceWidthInches);
      return p;
    }

    function onListboxKeydown(event, chips) {
      if (!chips.length) return;
      var keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '];
      if (keys.indexOf(event.key) < 0) return;
      event.preventDefault();
      var idx = -1;
      for (var i = 0; i < chips.length; i += 1) {
        if (chips[i].getAttribute('aria-selected') === 'true') idx = i;
      }
      if (idx < 0) idx = 0;
      var next = idx;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (idx + 1) % chips.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = (idx - 1 + chips.length) % chips.length;
      }
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = chips.length - 1;
      if (event.key === 'Enter' || event.key === ' ') {
        chips[idx].click();
        return;
      }
      var frameId = chips[next].getAttribute('data-frame-id');
      var frame = findFrame(frames, frameId);
      commitFrame(frame);
      var focusBtn = host.querySelector('.gw-frame-picker__chip.is-active');
      if (focusBtn) focusBtn.focus();
    }

    function render() {
      hidePopover();
      ensureSelection();
      host.innerHTML = '';
      activeOptionId = null;

      if (options.hint) {
        var hint = document.createElement('p');
        hint.className = 'gw-frame-picker__hint';
        hint.textContent = options.hint;
        host.appendChild(hint);
      }

      var seg = document.createElement('div');
      seg.className = 'gw-frame-picker__segment';
      seg.setAttribute('role', 'tablist');
      seg.setAttribute('aria-label', 'Frame tier');
      [
        { id: BAND_CLASSIC, label: 'Classic  3½–4″' },
        { id: BAND_ELITE, label: 'Elite  6½–7″' },
      ].forEach(function (item) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
          'gw-frame-picker__seg-btn' + (band === item.id ? ' is-active' : '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', band === item.id ? 'true' : 'false');
        btn.textContent = item.label;
        btn.addEventListener('click', function () {
          setBand(item.id);
        });
        seg.appendChild(btn);
      });
      host.appendChild(seg);

      var families = visibleFamilies();
      var listboxId = 'gw-frame-picker-list-' + density;
      var listbox = document.createElement('div');
      listbox.className = 'gw-frame-picker__list';
      listbox.setAttribute('role', 'listbox');
      listbox.setAttribute('aria-label', 'Frame finish');
      listbox.id = listboxId;
      listbox.tabIndex = 0;

      var groups =
        band === BAND_CLASSIC
          ? classicSubgroups(families)
          : [{ key: 'elite', label: null, families: families }];

      var chipEls = [];
      groups.forEach(function (group) {
        var section = document.createElement('div');
        section.className = 'gw-frame-picker__group';
        if (group.label) {
          var label = document.createElement('p');
          label.className = 'gw-frame-picker__group-label';
          label.textContent = group.label;
          section.appendChild(label);
        }
        var row = document.createElement('div');
        row.className = 'gw-frame-picker__chips';
        group.families.forEach(function (family) {
          var chip = chipButton(family, listboxId);
          chipEls.push(chip);
          row.appendChild(chip);
        });
        section.appendChild(row);
        listbox.appendChild(section);
      });

      listbox.addEventListener('keydown', function (event) {
        onListboxKeydown(event, chipEls);
      });
      if (activeOptionId) listbox.setAttribute('aria-activedescendant', activeOptionId);
      host.appendChild(listbox);

      var selectedFamily = findFamily(families, selectedId);
      var widthToggle = renderWidthToggle(selectedFamily);
      if (widthToggle) host.appendChild(widthToggle);
      var faceCaption = renderFaceCaption(selectedFamily);
      if (faceCaption) host.appendChild(faceCaption);

      if (selected) {
        var summary = document.createElement('p');
        summary.className = 'gw-frame-picker__summary';
        summary.setAttribute('aria-live', 'polite');
        var tier =
          band === BAND_ELITE
            ? 'Elite · ' + formatFaceWidth(selected.faceWidthInches)
            : selected.collection === 'Tabacchino'
              ? 'Classic · 4″'
              : 'Classic · 3½″';
        summary.textContent =
          'Selected · ' + finishFamilyFor(selected) + ' · ' + tier;
        if (!isAvailable(selected)) summary.textContent += ' · Preview only — not currently offered';
        host.appendChild(summary);
      }
    }

    function destroy() {
      hidePopover();
      host.innerHTML = '';
      host.classList.remove('gw-frame-picker', 'gw-frame-picker--pdp', 'gw-frame-picker--viz');
      host.removeAttribute('data-gw-frame-picker');
    }

    function setSelectedId(id) {
      selectedId = id;
      selected = findFrame(frames, id);
      if (selected) {
        band = bandFor(selected) || band;
        preferFace = selected.faceWidthInches;
      }
      render();
    }

    render();

    return {
      destroy: destroy,
      setSelectedId: setSelectedId,
      getSelectedId: function () {
        return selectedId;
      },
      getSelectedFrame: function () {
        return selected;
      },
      render: render,
    };
  }

  var api = {
    mount: mount,
    finishFamilyFor: finishFamilyFor,
    bandFor: bandFor,
    formatFaceWidth: formatFaceWidth,
    familiesForBand: familiesForBand,
    classicSubgroups: classicSubgroups,
    pickFrameInFamily: pickFrameInFamily,
    BAND_CLASSIC: BAND_CLASSIC,
    BAND_ELITE: BAND_ELITE,
  };

  root.GWFramePicker = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
